/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright (c) 2017 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property of
 * Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S.
 * and Foreign Patents, patent applications, and are protected by trade secret
 * or copyright law. Dissemination of this information, reproduction of this
 * material, and copying or distribution of this software is strictly forbidden
 * unless prior written permission is obtained from Pearson Education, Inc.
 */

import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import styles from "./_checkbox.module.scss";
import Icon from "../Icon/Icon.tsx";
import { generateId } from "./../_util/common.js";

const ENTER_KEY = 13;

class CheckBox extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      checked: props.checked || false,
      id: props.id || generateId(),
    };
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.checked !== null &&
      prevProps.checked !== this.props.checked
    ) {
      this.setState({ checked: this.props.checked });
    }
  }

  handleKeyEvents = (event) => {
    const key = event.which || event.keyCode || 0;
    switch (key) {
      case ENTER_KEY:
        event.preventDefault();
        this.handleChange(event, true);
        break;
      default:
        break;
    }
  };

  handleChange = (event, keypress = false) => {
    const val = keypress ? !event.target.checked : event.target.checked;
    if (this.props.checked === undefined) {
      this.setState({ checked: val }, () => {
        this.props.onCheckboxChange(val, event);
      });
    } else {
      this.props.onCheckboxChange(val, event);
    }
  };

  click = (e) => {
    e.stopPropagation();
  };

  render() {
    const sizeStyle = {
      width: `${this.props.size + "px"}`,
      height: `${this.props.size + "px"}`,
    };
    const container_height = {
      height: `${this.props.optionHeight + "px"}`,
    };
    const line_height = {
      lineHeight: `${this.props.size + "px"}`,
      paddingLeft: `${this.props.size + "px"}`,
    };
    let state_attribute = styles.ls_checkbox_label_enabled;
    let check_box_attribuet = styles.ls_checkbox_not_checked_not_disabled;
    let multi_correct = "#multi-correct-18"
    if(this.props.disabled){
      state_attribute = styles.ls_checkbox_label_disabled
      check_box_attribuet= styles.ls_checkbox_not_checked_disabled
      multi_correct = "#multi-correct-ns-18"
    }

    return (
      
      <div>
        {this.props.label && (
          <label className={styles.ls_checkbox_label}>{this.props.label}</label>
        )}
        <div style={container_height} className={styles.ls_container}>
          <input
            type="checkbox"
            checked={this.state.checked}
            disabled={this.props.disabled}
            id={this.state.id}
            onChange={this.handleChange}
            value={this.props.value}
            data-testid="checkbox_test"
            onKeyPress={this.handleKeyEvents}
            aria-checked={this.props.mixedState ? "mixed" : this.state.checked}
            onClick={(e) => this.click(e)}
          />
          <label
            className={`${styles.ls_checkbox_label_padding} ${state_attribute}`}
            htmlFor={this.state.id}
            onClick={(e) => this.click(e)}
            aria-label={this.props.ariaLabel ? this.props.ariaLabel : ""}
          >
            <span
              id={"checkbox-" + this.state.id}
              style={sizeStyle}
              className={styles.ls_checkbox}
            >
              {this.props.mixedState ? (
                <span className={styles.ls_checkbox_checked}>
                  <svg
                    style={{
                      width: "24px",
                      height: "24px",
                      left: "-2px ",
                      top: "-5px",
                    }}
                    focusable="false"
                    aria-hidden
                    role="img"
                  >
                    <use
                      xlinkHref={"#checkbox-tristate-24"}
                    />
                  </svg>
                </span>
              ) : this.state.checked ? (
                <span
                  className={
                    this.props.disabled
                      ? styles.ls_checkbox_checked_disabled
                      : styles.ls_checkbox_checked
                  }
                >
                  <svg
                    style={sizeStyle}
                    focusable="false"
                    aria-hidden
                    role="img"
                  >
                    <use
                      xlinkHref={`${multi_correct}`}
                    />
                  </svg>
                </span>
              ) : (
                <span
                  className={check_box_attribuet}
                >
                  <svg
                    style={sizeStyle}
                    focusable="false"
                    aria-hidden
                    role="img"
                  >
                    <use xlinkHref={"#multi-empty-18"} />
                  </svg>
                </span>
              )}
            </span>
            <span style={line_height} className={styles.ls_checkbox_font}>
              {this.props.children}
            </span>
          </label>
        </div>

        {this.props.infoText && (
          <div className={styles.ls_checkbox_infoText}>
            <label>{this.props.infoText}</label>
          </div>
        )}
        {this.props.isError && (
          <div className={styles.ls_checkbox_error}>
            <span>
              <Icon fileName="warning-16" focusable="false" isHidden={true} />
              <label>{this.props.errorMessage}</label>
            </span>
          </div>
        )}
      </div>
    );
  }
}

CheckBox.propTypes = {
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  errorMessage: PropTypes.string,
  groupname: PropTypes.string,
  id: PropTypes.string,
  infoText: PropTypes.string,
  isError: PropTypes.bool,
  label: PropTypes.string,
  value: PropTypes.string.isRequired,
  onCheckboxChange: PropTypes.func,
  size: PropTypes.string,
  optionHeight: PropTypes.string,
  children: PropTypes.any,
  mixedState: PropTypes.bool,
  ariaLabel: PropTypes.string,
};

CheckBox.defaultProps = {
  disabled: false,
  groupname: "checkbox_group",
  isError: false,
  onCheckboxChange: () => {/**/},
  size: "20",
  mixedState: false,
  id: generateId(),
};

export default CheckBox;
